import { Box, Button, Tooltip, Typography, useTheme } from '@mui/material';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { darkPrimary, lightPrimary } from 'src/colors';
import { Close } from 'src/icons/close';
import { Scrollbar } from '../scrollbar';

export interface BulkAction {
  id: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tooltip?: string;
  destructive?: boolean;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSelectAll: () => void;
  onPreviousSelection: () => void;
  actions: BulkAction[];
  selectedItemsCount: number;
  allItemsCount: number;
  showPreviousSelection: boolean;
  showSelectionButton?: boolean;
}

const BULK_ACTIONS_BUBBLE_WIDTH_WITHOUT_PADDING = 649;

const BULK_ACTIONS_BUBBLE_PADDING = 20;

const BulkActionsBubble: FC<Props> = ({
  isOpen,
  onClose,
  onSelectAll,
  onPreviousSelection,
  actions,
  selectedItemsCount,
  allItemsCount,
  showPreviousSelection,
  showSelectionButton = true,
}) => {
  const { t } = useTranslation();
  const {
    palette: { mode },
  } = useTheme();

  const actionsContainerRef = useRef<HTMLDivElement | null>(null);
  const staticContainerRef = useRef<HTMLDivElement | null>(null);

  const [shouldDisplayScrollbar, setShouldDisplayScrollbar] = useState(false);

  const calculateIfScrollbarShouldBeDisplayed = useCallback(() => {
    if (!staticContainerRef?.current || !actionsContainerRef?.current)
      return false;

    const actionsContainerAvailableWidth =
      BULK_ACTIONS_BUBBLE_WIDTH_WITHOUT_PADDING -
      staticContainerRef?.current?.clientWidth;

    if (
      actionsContainerRef?.current?.clientWidth > actionsContainerAvailableWidth
    ) {
      return true;
    }

    return false;
  }, []);

  useEffect(() => {
    if (actions.length > 0) {
      setShouldDisplayScrollbar(calculateIfScrollbarShouldBeDisplayed());
    }
  }, [actions, calculateIfScrollbarShouldBeDisplayed]);

  const renderActions = () => (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
      }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 2.5,
          flex: 1,
        }}
        ref={actionsContainerRef}>
        {actions?.map(action => (
          <Tooltip
            key={action.id}
            title={action.tooltip ?? ''}
            disableHoverListener={!action.disabled}>
            <span>
              <Button
                variant="text"
                size="small"
                onClick={action.onClick}
                disabled={action.disabled}
                sx={{
                  color: action.destructive
                    ? '#F1743C'
                    : mode === 'dark'
                      ? darkPrimary.contrast
                      : lightPrimary.contrast,
                  fontWeight: 600,
                  p: 0,
                  minWidth: 'max-content',
                  ':focus': {
                    borderRadius: 'unset',
                    boxShadow: 'unset',
                    color: mode === 'dark' ? '#888' : '#bbb',
                  },
                }}>
                {action.label}
              </Button>
            </span>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
      }}>
      <AnimatePresence initial={false}>
        {isOpen ? (
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: -20 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ type: 'spring' }}
            sx={{
              py: 1.6,
              position: 'fixed',
              zIndex: 99,
              bottom: 10,
              width: '100%',
              boxSizing: 'border-box',
              maxWidth:
                BULK_ACTIONS_BUBBLE_WIDTH_WITHOUT_PADDING +
                BULK_ACTIONS_BUBBLE_PADDING * 2,
              px: `${BULK_ACTIONS_BUBBLE_PADDING}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor:
                mode === 'dark' ? darkPrimary.main : lightPrimary.main,
              borderRadius: 100,
            }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}
              ref={staticContainerRef}>
              <Close
                onClick={onClose}
                sx={{
                  color:
                    mode === 'dark'
                      ? darkPrimary.contrast
                      : lightPrimary.contrast,
                  cursor: 'pointer',
                }}
              />
              <Typography
                color={
                  mode === 'dark' ? darkPrimary.contrast : lightPrimary.contrast
                }
                sx={{ lineHeight: 1.6, minWidth: 'max-content' }}
                variant="body2">
                {t('common:bulkActionsBubble.selected', {
                  count: selectedItemsCount,
                })}
              </Typography>

              {showSelectionButton ? (
                <Button
                  variant={'text'}
                  size="small"
                  onClick={() => {
                    if (showPreviousSelection) {
                      onPreviousSelection();
                      return;
                    }
                    onSelectAll();
                  }}
                  sx={{
                    color:
                      mode === 'dark'
                        ? darkPrimary.contrast
                        : lightPrimary.contrast,
                    fontSize: 14,
                    fontWeight: 400,
                    lineHeight: 1.6,
                    p: 0,
                    minWidth: 'max-content',
                    ':focus': {
                      borderRadius: 'unset',
                      boxShadow: 'unset',
                      color: mode === 'dark' ? '#888' : '#bbb',
                    },
                  }}>
                  {showPreviousSelection
                    ? t('common:bulkActionsBubble.previousSelection')
                    : t('common:bulkActionsBubble.selectAll', {
                        count: allItemsCount,
                      })}
                </Button>
              ) : null}
            </Box>
            {shouldDisplayScrollbar ? (
              <Scrollbar
                autoHide={false}
                style={{
                  display: 'flex',
                  flex: 1,
                  flexDirection: 'row',
                  overflowY: 'auto',
                  paddingBottom: 10,
                }}>
                {renderActions()}
              </Scrollbar>
            ) : (
              renderActions()
            )}
          </Box>
        ) : null}
      </AnimatePresence>
    </Box>
  );
};

export default BulkActionsBubble;
